/**
 * Marketing analytics & client-reporting glossary (Story 30.20) — the single
 * source for the /glossary page and its DefinedTermSet JSON-LD. Definitions are
 * concise, accurate, and self-contained (AI engines extract clean definitions
 * preferentially). `anchor` matches the rendered heading id.
 */
export interface GlossaryTerm {
  term: string;
  anchor: string;
  /** 2–3 sentence definition (the extractable unit). */
  definition: string;
}

export const GLOSSARY_TERMS: GlossaryTerm[] = [
  {
    term: "White-label reporting",
    anchor: "white-label-reporting",
    definition:
      "White-label reporting is the practice of delivering marketing reports and dashboards under your own agency's brand rather than the reporting tool's. It typically means custom logos, colors, and a custom domain, so clients see the report as coming directly from your agency.",
  },
  {
    term: "Client reporting automation",
    anchor: "client-reporting-automation",
    definition:
      "Client reporting automation is the use of software to pull marketing data from connected platforms and assemble it into reports on a schedule, without manual exports or copy-paste. It removes the recurring monthly effort of rebuilding the same charts for each client.",
  },
  {
    term: "Marketing dashboard",
    anchor: "marketing-dashboard",
    definition:
      "A marketing dashboard is a single view that combines key metrics from multiple platforms — such as ad spend, website traffic, and conversions — usually updated with live data. Agencies share dashboards with clients so they can check performance any time.",
  },
  {
    term: "KPI (Key Performance Indicator)",
    anchor: "kpi",
    definition:
      "A KPI, or Key Performance Indicator, is a specific, measurable metric used to track progress toward a goal — for example cost per lead or return on ad spend. Agencies pick a small set of KPIs per client to keep reports focused on what matters.",
  },
  {
    term: "MRR (Monthly Recurring Revenue)",
    anchor: "mrr",
    definition:
      "MRR, or Monthly Recurring Revenue, is the predictable revenue a subscription business earns each month from its active plans. It is a core metric for SaaS and agencies on retainer, and a key indicator of business health.",
  },
  {
    term: "ROAS (Return on Ad Spend)",
    anchor: "roas",
    definition:
      "ROAS, or Return on Ad Spend, is revenue generated divided by the amount spent on advertising, usually expressed as a ratio (e.g. 4:1). It tells you how much revenue each dollar of ad spend produced, and is a headline metric for paid-media reporting.",
  },
  {
    term: "ACOS (Advertising Cost of Sale)",
    anchor: "acos",
    definition:
      "ACOS, or Advertising Cost of Sale, is ad spend divided by attributed sales, expressed as a percentage — the inverse view of ROAS. It is most common in Amazon Ads reporting, where a lower ACOS means more efficient spend.",
  },
  {
    term: "CTR (Click-Through Rate)",
    anchor: "ctr",
    definition:
      "CTR, or Click-Through Rate, is the percentage of people who clicked an ad or link out of those who saw it (clicks divided by impressions). It measures how compelling a creative or listing is at earning a click.",
  },
  {
    term: "CPC (Cost Per Click)",
    anchor: "cpc",
    definition:
      "CPC, or Cost Per Click, is the average amount paid for each click on a paid ad (spend divided by clicks). It helps agencies compare the efficiency of campaigns and channels at driving traffic.",
  },
  {
    term: "CPM (Cost Per Mille)",
    anchor: "cpm",
    definition:
      "CPM, or Cost Per Mille, is the cost of one thousand ad impressions. It is used to compare the cost of reach across campaigns and platforms, especially for awareness-focused advertising.",
  },
  {
    term: "Conversion rate",
    anchor: "conversion-rate",
    definition:
      "Conversion rate is the percentage of visitors or clicks that complete a desired action — a purchase, a form submission, a call. It connects traffic to outcomes and is central to proving marketing effectiveness.",
  },
  {
    term: "Cost per conversion",
    anchor: "cost-per-conversion",
    definition:
      "Cost per conversion is the average spend required to generate one conversion (spend divided by conversions), sometimes called cost per acquisition (CPA). It is a bottom-line efficiency metric for performance campaigns.",
  },
  {
    term: "Data blending",
    anchor: "data-blending",
    definition:
      "Data blending is combining metrics from two or more sources into a single calculated figure — for example total spend across Google Ads and Meta Ads, or a blended cost per lead. It gives clients a unified view instead of siloed per-platform numbers.",
  },
  {
    term: "Custom metric",
    anchor: "custom-metric",
    definition:
      "A custom metric is a value you define with a formula over existing metrics — such as profit margin or a blended efficiency ratio — rather than a metric a platform provides directly. Reporting tools let agencies build these to match how each client thinks about performance.",
  },
  {
    term: "Model Context Protocol (MCP)",
    anchor: "model-context-protocol-mcp",
    definition:
      "The Model Context Protocol (MCP) is an open standard from Anthropic that lets an AI assistant like Claude securely connect to external tools and data sources. In marketing analytics, an MCP integration lets you ask an AI assistant about client performance and have it read your live reporting data.",
  },
  {
    term: "Attribution",
    anchor: "attribution",
    definition:
      "Attribution is the method of assigning credit for a conversion to the marketing touchpoints that influenced it. Models range from last-click (all credit to the final touch) to multi-touch (credit spread across touches), and the model chosen changes how channels appear to perform.",
  },
  {
    term: "Attribution window",
    anchor: "attribution-window",
    definition:
      "An attribution window is the period after an ad interaction during which a conversion is credited to that ad — for example a 7-day click window. Different platforms use different default windows, which is why the same conversion can be counted differently across tools.",
  },
  {
    term: "Impression share",
    anchor: "impression-share",
    definition:
      "Impression share is the percentage of the total available impressions your ads actually received in an auction-based platform. A low impression share signals lost opportunity, often due to budget or bid limits.",
  },
  {
    term: "Engaged session",
    anchor: "engaged-session",
    definition:
      "An engaged session, a Google Analytics 4 concept, is a visit that lasts longer than ten seconds, has a conversion event, or includes at least two page views. It is a stricter, more meaningful measure of a visit than a raw session count.",
  },
  {
    term: "Domain Authority (DA)",
    anchor: "domain-authority",
    definition:
      "Domain Authority (DA) is a 1–100 score from Moz that predicts how well a website will rank in search, based largely on its backlink profile. It is a comparative signal — useful for tracking a site against competitors, not an absolute ranking guarantee.",
  },
  {
    term: "Domain Rating (DR)",
    anchor: "domain-rating",
    definition:
      "Domain Rating (DR) is Ahrefs' 0–100 measure of the strength of a website's backlink profile relative to others. Like Domain Authority, it is a comparative link-authority signal, not a direct Google ranking factor.",
  },
  {
    term: "Core Web Vitals",
    anchor: "core-web-vitals",
    definition:
      "Core Web Vitals are a set of Google metrics measuring real-world user experience: Largest Contentful Paint (loading), Interaction to Next Paint (responsiveness), and Cumulative Layout Shift (visual stability). They are a ranking signal and a common site-health line item in SEO reports.",
  },
  {
    term: "AOV (Average Order Value)",
    anchor: "aov",
    definition:
      "AOV, or Average Order Value, is total revenue divided by the number of orders over a period. It is a core e-commerce metric — raising AOV increases revenue without needing more traffic.",
  },
  {
    term: "Open rate",
    anchor: "open-rate",
    definition:
      "Open rate is the percentage of delivered emails that recipients opened. Note that privacy features like Apple Mail Privacy Protection inflate reported opens, so it is best read as a trend rather than an absolute.",
  },
  {
    term: "Generative Engine Optimization (GEO)",
    anchor: "generative-engine-optimization-geo",
    definition:
      "Generative Engine Optimization (GEO) is the practice of structuring content so AI answer engines — ChatGPT, Perplexity, Claude, Google AI Overviews — can find, understand, and cite it. It complements traditional SEO, which targets ranked search results.",
  },
];
